// Imported `.cube` LUTs (§11.6), kept in IndexedDB by content hash; settings.lut.id points at one.
import { create } from 'zustand';
import { createStore, del, get, keys, set, type UseStore } from 'idb-keyval';
import { parseCube, type CubeLut } from './cube';

type Stored = { id: string; name: string; text: string };
let db: UseStore | null = null;
const luts = () => (db ??= createStore('epb-luts', 'luts'));
const parsed = new Map<string, CubeLut>();

export const useLuts = create<{ list: { id: string; name: string }[] }>(() => ({ list: [] }));

function hash(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return 'cube-' + (h >>> 0).toString(16).padStart(8, '0');
}

export async function refreshLuts() {
  try {
    const ids = (await keys(luts())) as string[];
    const rows = await Promise.all(ids.map((k) => get<Stored>(k, luts())));
    useLuts.setState({
      list: rows
        .filter((r): r is Stored => !!r)
        .map((r) => ({ id: r.id, name: r.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    });
  } catch {
    useLuts.setState({ list: [] });
  }
}

/** Parses (throws CubeError on a bad file) and stores a `.cube`; returns its id. */
export async function importCube(name: string, text: string): Promise<string> {
  const l = parseCube(text);
  const id = hash(text);
  parsed.set(id, l);
  await set(id, { id, name: l.title || name.replace(/\.cube$/i, ''), text } satisfies Stored, luts());
  await refreshLuts();
  return id;
}

export async function getLut(id: string): Promise<CubeLut | null> {
  const hit = parsed.get(id);
  if (hit) return hit;
  const row = await get<Stored>(id, luts()).catch(() => undefined);
  if (!row) return null;
  const l = parseCube(row.text);
  parsed.set(id, l);
  return l;
}

export async function deleteLut(id: string) {
  parsed.delete(id);
  await del(id, luts()).catch(() => undefined);
  await refreshLuts();
}
